import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "coachjp_install_banner_v1";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export default function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onInstalled = () => {
      try {
        api.post("/track", { kind: "app_installed", platform: detectPlatform() }).catch(() => {});
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ installed_at: Date.now() }));
      } catch {}
      setVisible(false);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      const state = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (state.installed_at) return;
      if (state.dismissed_at && Date.now() - state.dismissed_at < 7 * 24 * 3600 * 1000) return;
    } catch {}
    const p = detectPlatform();
    setPlatform(p);

    if (p === "android") {
      const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setVisible(true); };
      window.addEventListener("beforeinstallprompt", handler);
      const t = setTimeout(() => setVisible(true), 800);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }
    setVisible(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissed_at: Date.now() })); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "accepted") dismiss();
      setDeferredPrompt(null);
    } else {
      setShowHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-20 md:bottom-6 left-3 right-3 sm:left-auto sm:right-6 sm:max-w-sm z-40 bg-[#0a0a0a] border border-[#d7ff00] jp-shadow-glow p-4 flex items-start gap-3 fade-in" data-testid="install-banner">
        <img src="/icon-192.png" alt="Coach JP" className="w-12 h-12 rounded-lg flex-shrink-0 object-cover" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg uppercase leading-tight">Install Coach JP</div>
          <p className="text-xs text-[#bbb] mt-1">Add to your home screen for full-screen mode and faster opens.</p>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={install} className="bg-[#d7ff00] text-black font-display uppercase tracking-wider px-4 py-2 text-sm hover:bg-white flex items-center gap-1.5" data-testid="install-cta">
              <Download className="w-3.5 h-3.5" /> {platform === "ios" ? "How to install" : "Install"}
            </button>
            <button onClick={dismiss} className="text-[#888] text-xs font-mono-jp uppercase tracking-widest hover:text-white" data-testid="install-dismiss">Not now</button>
          </div>
        </div>
        <button onClick={dismiss} className="text-[#666] hover:text-white" aria-label="Close" data-testid="install-close"><X className="w-4 h-4" /></button>
      </div>

      {showHelp && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={() => setShowHelp(false)} data-testid="install-help-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-3">
              <div className="font-display text-2xl uppercase">Add to <span className="text-[#d7ff00]">home screen</span></div>
              <button onClick={() => setShowHelp(false)} className="text-[#888]" data-testid="install-help-close"><X className="w-5 h-5" /></button>
            </div>
            {platform === "ios" ? (
              <ol className="space-y-3 text-sm text-[#ddd]">
                <li className="flex gap-2"><span className="font-display text-[#d7ff00] text-lg leading-none">1.</span> Tap the <Share className="inline w-4 h-4 text-[#d7ff00]" /> Share icon at the bottom of Safari.</li>
                <li className="flex gap-2"><span className="font-display text-[#d7ff00] text-lg leading-none">2.</span> Scroll down and tap Add to Home Screen.</li>
                <li className="flex gap-2"><span className="font-display text-[#d7ff00] text-lg leading-none">3.</span> Tap Add in the top-right.</li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-[#ddd]">
                <li className="flex gap-2"><span className="font-display text-[#d7ff00] text-lg leading-none">1.</span> Open the browser menu.</li>
                <li className="flex gap-2"><span className="font-display text-[#d7ff00] text-lg leading-none">2.</span> Tap Add to Home screen (or Install app).</li>
                <li className="flex gap-2"><span className="font-display text-[#d7ff00] text-lg leading-none">3.</span> Confirm with Add.</li>
              </ol>
            )}
            <button onClick={() => { setShowHelp(false); dismiss(); }} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white" data-testid="install-help-got-it">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
